/**
 * Edpies — persistent XP / Level / Streak badge.
 *
 * Sits fixed top-right on every authenticated route. Single source of
 * truth = the backend `/api/gamification/profile` endpoint, so Level, XP
 * and Streak stay in sync with the dashboard widgets (instead of the
 * old localStorage-only implementation which always showed "Lv 1 · 0 XP"
 * for every brand-new device).
 *
 * Mobile (< 768 px): collapses to a 36 px circular pill (level number
 * only) so it doesn't block hamburger / nav. Tap to expand inline and
 * see Level · XP · Streak, then auto-collapses after 4 s.
 *
 * Desktop (≥ 768 px): full chip with progress ring + XP + flame streak.
 *
 * Optimistic XP gain: any code can fire
 *   window.dispatchEvent(new CustomEvent('edpies.xpEarned',{ detail:{ amount:15 } }))
 * which flashes "+15 XP" instantly while the backend is being refetched.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Flame } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const XP_PER_LEVEL = 100;

const readUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.id || u?.email || 'anon';
  } catch { return 'anon'; }
};
const cacheKey = (uid) => `edpies.gamProfile.${uid}`;

const loadCachedProfile = () => {
  try {
    const raw = localStorage.getItem(cacheKey(readUserId()));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const saveCachedProfile = (prof) => {
  try {
    localStorage.setItem(cacheKey(readUserId()), JSON.stringify(prof));
  } catch { /* ignore */ }
};

const XpLevelBadge = () => {
  const location = useLocation();
  const cached = loadCachedProfile();
  const [xp, setXp] = useState(cached?.xp?.total ?? 0);
  const [level, setLevel] = useState(cached?.level?.current?.level ?? 1);
  const [streak, setStreak] = useState(cached?.streak?.current ?? 0);
  const [flashAmount, setFlashAmount] = useState(0);
  const [expanded, setExpanded] = useState(false);   // mobile tap-to-expand
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );
  const flashTimerRef = useRef(null);
  const expandTimerRef = useRef(null);

  // ---- Fetch backend gamification profile ---------------------------------
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/gamification/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!data) return;
      const nextXp = data?.xp?.total ?? 0;
      const nextLevel = data?.level?.current?.level ?? 1;
      const nextStreak = data?.streak?.current ?? 0;
      setXp(nextXp);
      setLevel(nextLevel);
      setStreak(nextStreak);
      saveCachedProfile(data);
    } catch { /* silently keep stale cache */ }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Refresh after navigation events fire (Learning Task completion, mock
  // submission). Also refresh when the tab regains focus.
  useEffect(() => {
    const onXp = (e) => {
      const gain = Number(e?.detail?.amount) || 0;
      if (gain > 0) {
        setXp((prev) => prev + gain);
        setFlashAmount(gain);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setFlashAmount(0), 2400);
      }
      // Reconcile with backend in 1.2 s so the real Level / Streak catch up.
      setTimeout(fetchProfile, 1200);
    };
    const onFocus = () => fetchProfile();
    const onResize = () => setIsMobile(window.matchMedia('(max-width: 767px)').matches);
    window.addEventListener('edpies.xpEarned', onXp);
    window.addEventListener('focus', onFocus);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('edpies.xpEarned', onXp);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('resize', onResize);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
    };
  }, [fetchProfile]);

  // Don't render for logged-out visitors.
  const isAuthed = (() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  })();
  if (!isAuthed) return null;

  // Hide on the public marketing routes. Uses react-router's location so the
  // badge hides reactively on SPA client-side navigation (no full reload).
  const hideOn = ['/', '/auth', '/login', '/register', '/about', '/contact', '/faq', '/privacy', '/terms', '/pricing'];
  if (hideOn.includes(location.pathname)) return null;

  // ---- Render ---------------------------------------------------------------
  const xpInLevel = xp % XP_PER_LEVEL;
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - xpInLevel / XP_PER_LEVEL);

  const handleTap = () => {
    if (!isMobile) return;
    setExpanded((v) => !v);
    if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
    if (!expanded) {
      expandTimerRef.current = setTimeout(() => setExpanded(false), 4000);
    }
  };

  // MOBILE COLLAPSED — single 36 px pill (level number only)
  if (isMobile && !expanded) {
    return (
      <div
        className="fixed top-2 right-2 z-40"
        data-testid="xp-level-badge"
        aria-label={`Level ${level}, ${xp} XP, ${streak} day streak — tap to expand`}
      >
        <button
          type="button"
          onClick={handleTap}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-md border border-amber-300/60 active:scale-95 transition"
          data-testid="xp-badge-compact"
        >
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" width="36" height="36">
            <circle cx="18" cy="18" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3" />
            <circle
              cx="18" cy="18" r={radius} fill="none"
              stroke="#f59e0b" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            />
          </svg>
          <span className="relative text-[11px] font-bold text-amber-700 dark:text-amber-300">{level}</span>
        </button>
      </div>
    );
  }

  // EXPANDED / DESKTOP — full chip
  return (
    <div
      className="fixed top-2 right-2 sm:top-3 sm:right-3 z-40"
      data-testid="xp-level-badge"
      aria-label={`Level ${level}, ${xp} XP, ${streak} day streak`}
    >
      <div
        onClick={handleTap}
        className="inline-flex items-center gap-2 pl-1.5 pr-2.5 sm:pr-3 py-1 rounded-full bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-md border border-amber-300/60"
      >
        {/* Progress ring with level number */}
        <div className="relative h-8 w-8 flex items-center justify-center">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" width="32" height="32">
            <circle cx="18" cy="18" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3" />
            <circle
              cx="18" cy="18" r={radius} fill="none"
              stroke="#f59e0b" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}
            />
          </svg>
          <span className="relative text-[11px] font-bold text-amber-700 dark:text-amber-300" data-testid="xp-level-number">
            {level}
          </span>
        </div>

        {/* Compact XP + streak inline */}
        <div className="flex items-center gap-2 leading-none">
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100" data-testid="xp-total">
            <Zap className="inline h-3 w-3 -mt-0.5 text-amber-500 mr-0.5" />{xp}
          </span>
          <span
            className="inline-flex items-center text-xs font-semibold text-orange-700 dark:text-orange-300"
            data-testid="streak-count"
          >
            <Flame className="h-3 w-3 mr-0.5" />{streak}
          </span>
        </div>
      </div>

      {/* Floating "+15 XP" gain flash */}
      <AnimatePresence>
        {flashAmount > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 8, scale: 0.85 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 4, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
            className="absolute top-full right-0 mt-1.5 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[11px] font-bold shadow-md"
            data-testid="xp-flash"
          >
            +{flashAmount} XP
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default XpLevelBadge;
